/**
 * LeagueMembersPage — /league/:leagueId/members
 * ─────────────────────────────────────────────────────────────────
 * The Phase 2a flagship page. Lets a manager (or an administrator
 * with manage_members) invite users, assign them a league role
 * (manager / administrator / viewer), and configure granular
 * permissions on administrators via a checkbox grid of the 12
 * canonical permission keys.
 *
 * VALIDATES Phase 2 acceptance criterion #3:
 *   "A manager can invite an administrator and grant 3 specific
 *    permissions; that administrator logs in and sees ONLY those
 *    3 sections in the league nav."
 *
 * The page also surfaces the privilege-escalation guards from the
 * backend in the UI:
 *   • The "manager" role option is hidden for non-manager callers.
 *   • The Last-Manager floor is reflected: demoting/removing the
 *     last manager shows the backend's 400 error inline.
 *
 * Add-member identifiers:
 *   • Email      — the user's account email; hashed server-side
 *   • Player UID — PLR-XXXXXXXX shareable from the user's profile
 *   Exactly one is sent per request; backend resolves to user_id.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, Crown, Shield, Eye, X, Save, AlertTriangle, Mail, Hash, Check } from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import {
  useLeagueMembership,
  invalidateLeagueMembershipCache,
  LEAGUE_PERMISSION_KEYS,
  PERMISSION_LABELS,
} from '../../hooks/useLeagueMembership.js';

// Visual treatment per role. Matches LeagueListPage for consistency.
const ROLE_META = {
  manager:       { Icon: Crown,  color: 'var(--puck)',   label: 'Manager' },
  administrator: { Icon: Shield, color: 'var(--ice)',    label: 'Administrator' },
  viewer:        { Icon: Eye,    color: 'var(--text-2)', label: 'Viewer' },
};

export default function LeagueMembersPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);

  const [league,  setLeague]  = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit modal — opens for a specific member row. null = closed.
  const [editing, setEditing] = useState(null);
  // Invite modal state — separate concern from editing.
  const [showInvite, setShowInvite] = useState(false);

  async function loadAll() {
    if (!leagueId) return;
    try {
      const [leagueRes, membersRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/members`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setMembers(membersRes?.data?.members || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load members');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leagueId]);

  const canManageMembers = isManager || can('manage_members');

  // PHASE 2a — show all 12 permissions in the grid even for permissions
  // whose target features haven't shipped yet. The Manager configures
  // permissions in anticipation of later phases; the API stores them
  // exactly as set. When a later phase ships, the existing permission
  // grants are already in effect — no migration of permission data needed.
  const allPermKeys = LEAGUE_PERMISSION_KEYS;

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 980 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 980 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 980 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 className="section-title text-xl">Members ({members.length})</h2>
        {canManageMembers && (
          <button
            type="button"
            className="btn-ice"
            onClick={() => setShowInvite(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <Plus size={14} />
            Invite member
          </button>
        )}
      </div>

      {!canManageMembers && (
        <div className="card" style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-2)' }}>
          You can see who's in the league but can't add or change members. The{' '}
          <code>manage_members</code> permission is required for changes.
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {members.map((m, idx) => (
          <MemberRow
            key={m.id}
            member={m}
            isLast={idx === members.length - 1}
            canEdit={canManageMembers}
            canEditManager={isManager}   // only managers can edit a manager
            onEdit={() => setEditing(m)}
          />
        ))}
      </div>

      {showInvite && (
        <InviteModal
          isManager={isManager}
          allPermKeys={allPermKeys}
          onClose={() => setShowInvite(false)}
          onCreated={async () => {
            setShowInvite(false);
            await loadAll();
            invalidateLeagueMembershipCache(leagueId);
          }}
          leagueId={leagueId}
        />
      )}

      {editing && (
        <EditMemberModal
          member={editing}
          isManager={isManager}
          allPermKeys={allPermKeys}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await loadAll();
            invalidateLeagueMembershipCache(leagueId);
          }}
          onRemoved={async () => {
            setEditing(null);
            await loadAll();
            invalidateLeagueMembershipCache(leagueId);
          }}
          leagueId={leagueId}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Member list row
// ───────────────────────────────────────────────────────────────

function MemberRow({ member, isLast, canEdit, canEditManager, onEdit }) {
  const meta = ROLE_META[member.member_role] || ROLE_META.viewer;
  const Icon = meta.Icon;
  // Count enabled permissions for administrators (cosmetic — shown next to badge).
  const enabledPermCount = useMemo(() => {
    if (member.member_role !== 'administrator') return null;
    const p = member.permissions_json || {};
    return Object.values(p).filter(v => v === true).length;
  }, [member]);

  // An administrator with manage_members can edit other members but
  // NOT a manager. Use this to disable the row's edit button.
  const isManagerRow = member.member_role === 'manager';
  const editDisabled = !canEdit || (isManagerRow && !canEditManager);

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: 'var(--surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, color: 'var(--text-2)', fontWeight: 700, fontSize: 14,
      }}>
        {getInitials(member.display_name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>
          {member.display_name || '(unknown)'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
          Account: <span style={{ textTransform: 'capitalize' }}>{member.account_role}</span>
          {enabledPermCount !== null && (
            <> · {enabledPermCount} of {LEAGUE_PERMISSION_KEYS.length} permissions</>
          )}
        </div>
      </div>
      <span
        className="badge"
        style={{
          background: 'transparent',
          color: meta.color,
          border: `1px solid ${meta.color}`,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', fontSize: 11,
        }}
      >
        <Icon size={11} />
        {meta.label}
      </span>
      <button
        type="button"
        onClick={onEdit}
        disabled={editDisabled}
        className="btn-ghost"
        style={{
          padding: '6px 12px', fontSize: 12,
          opacity: editDisabled ? 0.4 : 1,
          cursor: editDisabled ? 'not-allowed' : 'pointer',
        }}
        title={editDisabled
          ? (isManagerRow ? 'Only managers can edit a manager' : 'You don\'t have permission to edit')
          : 'Edit role and permissions'}
      >
        Edit
      </button>
    </div>
  );
}

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || '?';
}

// ───────────────────────────────────────────────────────────────
// Invite modal
// ───────────────────────────────────────────────────────────────

function InviteModal({ isManager, allPermKeys, onClose, onCreated, leagueId }) {
  // Two input modes — radio between Email and Player UID.
  const [mode, setMode]   = useState('email');
  const [email, setEmail] = useState('');
  const [uid, setUid]     = useState('');
  // Selected role — managers can pick all 3; admins can only pick admin/viewer.
  const [role, setRole]   = useState('viewer');
  const [perms, setPerms] = useState({});  // only meaningful when role='administrator'
  const [saving, setSaving] = useState(false);

  // When role flips away from administrator, blank the perms map so
  // it doesn't get sent unnecessarily.
  useEffect(() => {
    if (role !== 'administrator') setPerms({});
  }, [role]);

  async function handleSubmit() {
    const body = { member_role: role };
    if (mode === 'email') {
      if (!email.trim() || !email.includes('@')) {
        toast.error('Please enter a valid email');
        return;
      }
      body.email = email.trim();
    } else {
      const trimmed = uid.trim().toUpperCase();
      if (!/^PLR-[A-Z0-9]{8}$/.test(trimmed)) {
        toast.error('Player UID looks like PLR-XXXXXXXX (8 chars after the dash)');
        return;
      }
      body.player_uid = trimmed;
    }
    if (role === 'administrator') body.permissions = perms;
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/members`, body);
      toast.success(`Invited as ${ROLE_META[role].label.toLowerCase()}`);
      onCreated();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not invite';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile-h (not 100vh) — on mobile Safari/Capacitor, 100vh doesn't
          shrink when the keyboard opens (email/UID inputs below), so a
          plain '90vh' can push the Invite button off-screen. */}
      <div className="card" style={{ maxWidth: 580, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <ModalHeader title="Invite member" onClose={onClose} />

        {/* Identifier mode */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <ModeButton active={mode === 'email'} onClick={() => setMode('email')} icon={Mail}>By email</ModeButton>
          <ModeButton active={mode === 'uid'}   onClick={() => setMode('uid')}   icon={Hash}>By Player UID</ModeButton>
        </div>

        {mode === 'email' ? (
          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={fieldLabelStyle}>Account email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              style={inputStyle}
              autoFocus
            />
            <span style={fieldHintStyle}>
              The user must already have a Game Day account. We hash the email server-side and look up
              their account — we never store this address.
            </span>
          </label>
        ) : (
          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={fieldLabelStyle}>Player UID</span>
            <input
              type="text"
              value={uid}
              onChange={(e) => setUid(e.target.value.toUpperCase())}
              placeholder="PLR-XXXXXXXX"
              maxLength={12}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}
              autoFocus
            />
            <span style={fieldHintStyle}>
              Ask the user for the UID from their Game Day profile. It's stable across email changes
              and never reveals their personal info.
            </span>
          </label>
        )}

        {/* Role selection */}
        <div style={{ marginBottom: 16 }}>
          <div style={fieldLabelStyle}>League role</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <RoleRadio
              value="viewer" label="Viewer"
              description="Read-only access to the league dashboard, schedule, and standings."
              role={role} onChange={setRole}
            />
            <RoleRadio
              value="administrator" label="Administrator"
              description="Delegate with the specific permissions you grant below."
              role={role} onChange={setRole}
            />
            {isManager && (
              <RoleRadio
                value="manager" label="Manager"
                description="Full league control, including billing and the ability to grant permissions."
                role={role} onChange={setRole}
              />
            )}
          </div>
        </div>

        {/* Permission grid — only shown for administrator */}
        {role === 'administrator' && (
          <PermissionGrid
            allPermKeys={allPermKeys}
            value={perms}
            onChange={setPerms}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="btn-ice"
            onClick={handleSubmit}
            disabled={saving || (mode === 'email' ? !email.trim() : !uid.trim())}
          >
            {saving ? 'Inviting…' : 'Invite'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ───────────────────────────────────────────────────────────────
// Edit member modal
// ───────────────────────────────────────────────────────────────

function EditMemberModal({ member, isManager, allPermKeys, onClose, onSaved, onRemoved, leagueId }) {
  const [role, setRole]   = useState(member.member_role);
  const [perms, setPerms] = useState(member.permissions_json || {});
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');  // optional audit reason

  // If the user picks anything other than administrator, clear the
  // perm map so we don't send stale checked-keys to the server.
  useEffect(() => {
    if (role !== 'administrator') setPerms({});
  }, [role]);

  // Determine whether the user can grant 'manager' here. Only an
  // existing manager can; an admin with manage_members editing a
  // viewer/admin doesn't see the 'manager' option.
  const showManagerOption = isManager;

  async function handleSave() {
    setSaving(true);
    try {
      const body = { member_role: role };
      if (role === 'administrator') body.permissions = perms;
      if (reason.trim()) body.reason = reason.trim();
      await api.patch(`/leagues/${leagueId}/members/${member.user_id}`, body);
      toast.success('Member updated');
      onSaved();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    const ok = window.confirm(`Remove ${member.display_name} from the league?`);
    if (!ok) return;
    setRemoving(true);
    try {
      await api.delete(`/leagues/${leagueId}/members/${member.user_id}`);
      toast.success('Member removed');
      onRemoved();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not remove';
      toast.error(msg);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile-h (not 100vh) — same keyboard-clipping fix as InviteModal
          above (the "Reason" input can push the Save/Remove footer down). */}
      <div className="card" style={{ maxWidth: 580, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }}>
        <ModalHeader title={`Edit ${member.display_name}`} onClose={onClose} />

        <div style={{ marginBottom: 16 }}>
          <div style={fieldLabelStyle}>League role</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <RoleRadio value="viewer" label="Viewer"
              description="Read-only access."
              role={role} onChange={setRole} />
            <RoleRadio value="administrator" label="Administrator"
              description="Delegate with specific permissions."
              role={role} onChange={setRole} />
            {showManagerOption && (
              <RoleRadio value="manager" label="Manager"
                description="Full league control."
                role={role} onChange={setRole} />
            )}
          </div>
        </div>

        {role === 'administrator' && (
          <PermissionGrid allPermKeys={allPermKeys} value={perms} onChange={setPerms} />
        )}

        <label style={{ display: 'block', marginTop: 12, marginBottom: 16 }}>
          <span style={fieldLabelStyle}>Reason (optional, for audit log)</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. 'Promoted to handle schedule'"
            maxLength={200}
            style={inputStyle}
          />
        </label>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-ghost"
            onClick={handleRemove}
            disabled={saving || removing}
            style={{ color: 'var(--penalty)', borderColor: 'var(--penalty-a30)' }}
          >
            <Trash2 size={13} style={{ marginRight: 6 }} />
            {removing ? 'Removing…' : 'Remove from league'}
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn-ghost" onClick={onClose} disabled={saving || removing}>
              Cancel
            </button>
            <button type="button" className="btn-ice" onClick={handleSave} disabled={saving || removing}>
              <Save size={13} style={{ marginRight: 6 }} />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ───────────────────────────────────────────────────────────────
// Permission grid
// ───────────────────────────────────────────────────────────────

function PermissionGrid({ allPermKeys, value, onChange }) {
  function toggle(key) {
    onChange({ ...value, [key]: !value[key] });
  }
  function allOn() {
    const next = {};
    for (const k of allPermKeys) next[k] = true;
    onChange(next);
  }
  function allOff() {
    onChange({});
  }
  const enabledCount = Object.values(value || {}).filter(v => v === true).length;

  return (
    <div style={{
      background: 'var(--surface-2)', borderRadius: 12,
      padding: 14, marginBottom: 4,
      border: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Permissions</div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
            {enabledCount} of {allPermKeys.length} granted. Each maps to a section of the league UI
            and to backend write permissions.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={allOn}>All</button>
          <button type="button" className="btn-ghost" style={tinyBtn} onClick={allOff}>None</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
        {allPermKeys.map((key) => {
          const on = value?.[key] === true;
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                background: on ? 'var(--ice-a10)' : 'transparent',
                border: `1px solid ${on ? 'var(--ice-a40)' : 'var(--border)'}`,
                color: on ? 'var(--text)' : 'var(--text-2)',
                fontSize: 12, fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 4,
                background: on ? 'var(--ice)' : 'var(--surface)',
                border: `1px solid ${on ? 'var(--ice)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {on && <Check size={10} color="var(--btn-accent-text)" strokeWidth={3} />}
              </span>
              <span style={{ flex: 1 }}>
                <div style={{ lineHeight: 1.2 }}>{PERMISSION_LABELS[key]}</div>
                <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                  {key}
                </div>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Shared small bits
// ───────────────────────────────────────────────────────────────

function ModalBackdrop({ onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, zIndex: 100,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <h2 className="section-title text-lg">{title}</h2>
      <button
        type="button"
        onClick={onClose}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: 4 }}
        aria-label="Close"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function ModeButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, padding: '8px 12px', borderRadius: 8,
        background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
        border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
        color: active ? 'var(--text)' : 'var(--text-2)',
        fontWeight: 600, fontSize: 13, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

function RoleRadio({ value, label, description, role, onChange }) {
  const meta = ROLE_META[value];
  const Icon = meta?.Icon || Eye;
  const active = role === value;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: 12, borderRadius: 10,
        background: active ? 'var(--surface-2)' : 'transparent',
        border: `1px solid ${active ? (meta?.color || 'var(--border-hover)') : 'var(--border)'}`,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        marginTop: 2, flexShrink: 0,
        background: active ? (meta?.color || 'var(--ice)') : 'transparent',
        border: `1.5px solid ${active ? (meta?.color || 'var(--ice)') : 'var(--text-2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {active && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--btn-accent-text)' }} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={14} color={meta?.color} />
          <span style={{ fontWeight: 700, fontSize: 14 }}>{label}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{description}</div>
      </div>
    </button>
  );
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 14px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  outline: 'none',
};
const fieldLabelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 };
const fieldHintStyle  = { display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 4 };
const tinyBtn = { padding: '4px 10px', fontSize: 11, minHeight: 'auto' };
